/**
 * answerUnits.ts — WEB-2350 (решение владельца 03.11 «рек»): единицы ответа
 * «Веса» и «Роста» в правилах логики.
 *
 * Ответ посетителя у этих блоков — голое число в ЕГО единице (единица лежит
 * отдельным ключом `<id>::unit`, `answerCollection.ts`; нет ключа — единица
 * блока по умолчанию). Условие помнит единицы автора (`unit`, нет поля —
 * единица блока по умолчанию). Перед сравнением ответ переводится в единицы
 * условия и округляется до одного знака (`roundTo1`) — иначе «= 80 кг» не
 * совпало бы ни с одним вводом в фунтах.
 *
 * Чистый модуль (без React и DOM). Переводчики — из лёгкого
 * `player/unitConversion.ts` (те же числа отдаёт `bodyMetrics.ts`, второй
 * таблицы нет; сам `bodyMetrics` едет отложенным куском и в первый экран не
 * тянется). Единица блока по
 * умолчанию приходит функцией (`resolvePickerUnitDefault` у плеера) — движок
 * не знает устройства блоков.
 *
 * Граница: карту собирает только плеер квиза (`QuizScreenWizard`). Пейволл и
 * экран после оплаты её не получают — у них сравнивается голое число, как
 * до карточки.
 */
import {
  CM_PER_INCH,
  roundTo1,
  SLIMKIT_LB_PER_KG,
} from '../player/unitConversion';
import type { ConditionUnit } from './rule.schema';

/** Единица ответа посетителя и единица блока по умолчанию. */
export interface AnswerUnit {
  visitor: ConditionUnit;
  block: ConditionUnit;
}

const MEASURE_TYPES: Record<string, readonly ConditionUnit[]> = {
  weight_picker: ['kg', 'lb'],
  height_picker: ['cm', 'ft'],
};

const UNIT_KEY = '::unit';
const INCHES_PER_FOOT = 12;

function asUnit(
  raw: unknown,
  allowed: readonly ConditionUnit[],
): ConditionUnit | null {
  // Без zod: движок вычисления не тянет схему в первый экран воронки.
  return typeof raw === 'string' && (allowed as readonly string[]).includes(raw)
    ? (raw as ConditionUnit)
    : null;
}

/**
 * Карта «блок → единицы» по экранам и ответам: для каждого «Веса» и «Роста» —
 * единица посетителя (`<id>::unit`, иначе единица блока) и единица блока.
 */
export function collectAnswerUnits<B extends { id: string; type: string }>(
  screens: readonly { blocks?: readonly B[] }[],
  values: Readonly<Record<string, string | undefined>>,
  defaultUnitOf: (block: B) => string,
): Record<string, AnswerUnit> {
  const out: Record<string, AnswerUnit> = {};
  for (const screen of screens) {
    for (const block of screen.blocks ?? []) {
      const allowed = MEASURE_TYPES[block.type];
      if (!allowed) continue;
      const blockUnit = asUnit(defaultUnitOf(block), allowed) ?? allowed[0];
      const visitor =
        asUnit(values[`${block.id}${UNIT_KEY}`], allowed) ?? blockUnit;
      out[block.id] = { visitor, block: blockUnit };
    }
  }
  return out;
}

/** Значение в килограммах или сантиметрах. */
function toBase(value: number, unit: ConditionUnit): number {
  if (unit === 'lb') return value / SLIMKIT_LB_PER_KG;
  if (unit === 'ft') return value * INCHES_PER_FOOT * CM_PER_INCH;
  return value;
}

function fromBase(value: number, unit: ConditionUnit): number {
  if (unit === 'lb') return value * SLIMKIT_LB_PER_KG;
  if (unit === 'ft') return value / (INCHES_PER_FOOT * CM_PER_INCH);
  return value;
}

const DIMENSION: Record<ConditionUnit, 'weight' | 'height'> = {
  kg: 'weight',
  lb: 'weight',
  cm: 'height',
  ft: 'height',
};

/**
 * Ответ посетителя в единицах условия. Нет записи в карте, ответ не число
 * или единицы разной природы — ответ как есть (голое число, как раньше).
 */
export function answerInRuleUnit(
  answer: string | string[] | null | undefined,
  ruleUnit: ConditionUnit | undefined,
  entry: AnswerUnit | undefined,
): string | string[] | null | undefined {
  if (!entry || typeof answer !== 'string' || answer.trim() === '')
    return answer;
  const target = ruleUnit ?? entry.block;
  if (
    target === entry.visitor ||
    DIMENSION[target] !== DIMENSION[entry.visitor]
  )
    return answer;
  const n = Number(answer.trim().replace(',', '.'));
  if (!Number.isFinite(n)) return answer;
  return String(roundTo1(fromBase(toBase(n, entry.visitor), target)));
}
